import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { checkZip } from '../data/zipData';
import { playCoinSound } from '../utils/coinSound';
import { useAddressAutocomplete } from '../utils/useAddressAutocomplete';
import { fetchAllSolarData } from '../utils/solarApi';
import CAMap from '../components/CAMap';

const WEB3FORMS_KEY = '900b5fca-4b67-439d-a812-5d10f3a1cdda';

function fmt(n) { return '$' + Math.round(n).toLocaleString(); }
function fmtDec(n, d = 1) { return n.toFixed(d); }
function fmtKWh(n) { return Math.round(n).toLocaleString() + ' kWh'; }

function calcIRR(investment, cashflow, years) {
  let rate = 0.1;
  for (let i = 0; i < 1000; i++) {
    let npv = -investment, dnpv = 0;
    for (let t = 1; t <= years; t++) {
      npv += cashflow / Math.pow(1 + rate, t);
      dnpv -= t * cashflow / Math.pow(1 + rate, t + 1);
    }
    const nr = rate - npv / dnpv;
    if (Math.abs(nr - rate) < 0.0001) break;
    rate = nr;
  }
  return rate * 100;
}

const inputStyle = {
  width: '100%', padding: '0.875rem 1rem', borderRadius: 10,
  border: '1.5px solid var(--gray-200)', fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.95rem', color: 'var(--navy)', background: 'var(--gray-50)', outline: 'none',
};
const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.4rem' };

function MetricCard({ label, value, gold, big, green, amber, sub }) {
  return (
    <div style={{ padding: '1.25rem', borderRadius: 12, background: 'var(--gray-50)', border: '1.5px solid var(--gray-100)' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>{label}</div>
      <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: big ? '1.85rem' : '1.5rem', color: gold ? 'var(--gold)' : green ? 'var(--green)' : amber ? 'var(--amber)' : 'var(--navy)' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>{sub}</div>}
    </div>
  );
}

function MonthlyChart({ data }) {
  const [hovered, setHovered] = React.useState(null);
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.kwh));
  return (
    <div>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>Monthly Generation (kWh)</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 88 }}>
        {data.map(({ month, kwh }, i) => (
          <div key={month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, position: 'relative' }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            {hovered === i && (
              <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', background: 'var(--navy)', color: '#fff', fontSize: '0.62rem', fontWeight: 700, padding: '3px 6px', borderRadius: 5, whiteSpace: 'nowrap', marginBottom: 4, zIndex: 10, pointerEvents: 'none' }}>
                {kwh.toLocaleString()}
              </div>
            )}
            <div style={{ width: '100%', borderRadius: '4px 4px 0 0', background: hovered === i ? 'var(--navy)' : 'var(--gold)', height: Math.max(4, (kwh / max) * 64), transition: 'background 0.15s' }} />
            <span style={{ fontSize: '0.6rem', color: hovered === i ? 'var(--navy)' : 'var(--gray-400)', fontWeight: 600, transition: 'color 0.15s' }}>{month.slice(0,1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Commercial() {
  const [form, setForm] = useState({ address: '', city: '', zip: '', monthlyBill: '', financing: 'full', company: '', contact: '', email: '', phone: '' });
  const [coordsRef] = useState({ lat: null, lng: null });
  const [zipInfo, setZipInfo] = useState(null);
  const [consent, setConsent] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [incentivesOpen, setIncentivesOpen] = useState(false);
  const addressRef = useRef(null);

  const handleAddressSelect = useCallback(({ address, city, zip }) => {
    const info = zip.length === 5 ? checkZip(zip) : null;
    setZipInfo(info);
    setForm(f => ({ ...f, address, city, zip }));
  }, []);

  const { suggestions, open: suggestOpen, handleInput: handleAddrInput, handleSelect: handleAddrSelect, containerRef: addrContainerRef, dropdownRect, getMainText, getSecondaryText } = useAddressAutocomplete(addressRef, handleAddressSelect);

  const handleZipBlur = () => {
    if (form.zip.length === 5 && /^\d{5}$/.test(form.zip)) setZipInfo(checkZip(form.zip));
  };

  const handleSubmit = async () => {
    if (!form.monthlyBill || parseFloat(form.monthlyBill) < 100) { alert('Please enter a monthly bill of at least $100.'); return; }
    if (!form.address) { alert('Please enter your facility address.'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.email && !emailRegex.test(form.email)) { alert('Please enter a valid email address (e.g. john@company.com).'); return; }
    if (!form.email) { alert('Please enter your email address.'); return; }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (form.phone && phoneDigits.length < 10) { alert('Please enter a valid 10-digit US phone number.'); return; }
    playCoinSound();
    setLoading(true); setResults(null);

    let bundle = null;
    try {
      setLoadingMsg('📡 Fetching location & utility rate data...');
      bundle = await fetchAllSolarData(form.address, 10, form.zip);
      coordsRef.lat = bundle.lat;
      coordsRef.lng = bundle.lng;
    } catch (e) {
      console.warn('API fetch failed, using fallback estimates:', e.message);
    }
    setLoadingMsg('⚡ Running 25-year financial model...');
    await new Promise(r => setTimeout(r, 800));

    const monthlyBill = parseFloat(form.monthlyBill);
    const isLowIncome = zipInfo?.isLowIncome || false;
    const isFireHazard = zipInfo?.isFireHazard || false;

    const UTILITY_RATE = bundle?.rate?.avgRatePerKWh || 0.18;
    const utilityName  = bundle?.rate?.utilityName  || 'CA Average';
    const SUN_HOURS    = 1400; // CA average fallback
    const COST_PER_WATT = 2.00;
    const COST_PER_KWH_STORAGE = 800;

    const monthlyKWh   = monthlyBill / UTILITY_RATE;
    const annualKWh    = monthlyKWh * 12;
    const systemSizeKW = annualKWh / SUN_HOURS; // raw size from usage

    // Use NREL PVWatts actual generation if available, else estimate
    let actualAnnualKWh = bundle?.pv?.annualKWh || (systemSizeKW * SUN_HOURS);
    let monthlyPV = bundle?.pv?.monthlyData || null;
    let pvCapacityFactor = bundle?.pv?.capacityFactor || null;

    // If PVWatts was called with default 10kW, scale to actual system size
    if (bundle?.pv?.annualKWh && systemSizeKW !== 10) {
      const scale = systemSizeKW / 10;
      actualAnnualKWh = Math.round(bundle.pv.annualKWh * scale);
      monthlyPV = bundle.pv.monthlyData?.map(d => ({ ...d, kwh: Math.round(d.kwh * scale) }));
    }

    const systemCost = systemSizeKW * 0.75 * 1000 * COST_PER_WATT; // 75% install ratio
    const systemCapacityKWh = systemCost / COST_PER_KWH_STORAGE;
    const ITC = systemCost * 0.40;
    const bonusDepreciation = systemCost * 0.85 * 0.21;
    const sgipFlaggedForReview = (isLowIncome || isFireHazard);
    const totalIncentives = ITC + bonusDepreciation;
    const netSystemCost = systemCost - totalIncentives;
    const annualElecSavings = actualAnnualKWh * UTILITY_RATE;
    const annualLCFS = systemCapacityKWh * 365 * 0.80 * 0.08;
    const annualTotalBenefit = annualElecSavings + annualLCFS;
    const annualTotalBenefitAdj = annualTotalBenefit * 0.75; // conservative factor: degradation, curtailment, rate variance
    const paybackYears = netSystemCost / annualTotalBenefitAdj;
    const savings25yr = (annualTotalBenefitAdj * 25) - netSystemCost;
    const IRR25yr = calcIRR(netSystemCost, annualTotalBenefitAdj, 25);
    const monthlyRate = 0.06 / 12;
    const monthlyPayment = netSystemCost * (monthlyRate * Math.pow(1 + monthlyRate, 120)) / (Math.pow(1 + monthlyRate, 120) - 1);
    const monthlyNetCashFlow = (annualTotalBenefitAdj / 12) - monthlyPayment;
    const carbonTons25yr = Math.round(actualAnnualKWh / 1000 * 0.386 * 25 * 10) / 10;
    const dataSource = bundle ? `NREL PVWatts + ${utilityName}` : 'Estimated (API unavailable)';

    const r = {
      systemSizeKW, systemCost, ITC, bonusDepreciation, totalIncentives, netSystemCost,
      annualElecSavings, annualLCFS, annualTotalBenefit: annualTotalBenefitAdj, paybackYears, savings25yr,
      IRR25yr, monthlyPayment, monthlyNetCashFlow,
      isLoan: form.financing === 'loan', sgipFlaggedForReview,
      utilityName, UTILITY_RATE, actualAnnualKWh,
      pvCapacityFactor, monthlyPV, carbonTons25yr, dataSource,
    };
    setResults(r);
    setLoading(false);
    setLoadingMsg('');

    fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject: `[SolarSavings] New Commercial Lead — ${form.businessName || form.fullName}`,
        from_name: form.fullName,
        replyto: form.email,
        'Form Type': 'Commercial Calculator',
        Name: form.fullName, Email: form.email, Phone: form.phone,
        'Business Name': form.businessName || '',
        Address: form.address, 'Monthly Bill': form.monthlyBill,
        'System Size (kW)': fmtDec(systemSizeKW),
        'Annual Generation': fmtKWh(actualAnnualKWh),
        Utility: utilityName, 'Rate ($/kWh)': UTILITY_RATE,
        'Net Cost': fmt(netSystemCost),
        'Payback Years': fmtDec(paybackYears),
        '25yr Savings': fmt(savings25yr),
        'IRR': IRR25yr ? fmtDec(IRR25yr) + '%' : 'N/A',
        'Data Source': dataSource,
      }),
    }).then(res => res.json()).then(data => {
      if (!data.success) console.warn('[Web3Forms] Commercial error:', data);
    }).catch(err => console.warn('[Web3Forms] Network error:', err));
  };

  return (
    <>
      <Helmet>
        <title>Commercial Solar California – ROI Analysis for Businesses in 2026 | SolarSavings</title>
        <meta name="description" content="California businesses can recover ~50% of commercial solar system cost in year one through the 30% federal ITC and 100% bonus depreciation. Free ROI analysis — July 4, 2026 deadline approaching." />
        <link rel="canonical" href="https://solarsavings.com/commercial" />
      </Helmet>
      {/* HERO */}
      <div style={{ height: '42vh', minHeight: 320, background: 'var(--navy)', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden', padding: '0 2.5rem', paddingTop: '5rem' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-conic-gradient(rgba(245,166,35,0.05) 0% 25%, transparent 0% 50%)', backgroundSize: '40px 40px' }} />
        <div style={{ position: 'absolute', right: -100, top: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 70%)' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {['✓ NREL PVWatts', '✓ Live Utility Rates', '✓ ITC 40% + Bonus Depreciation', '✓ LCFS Income'].map(t => (
              <span key={t} style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)' }}>{t}</span>
            ))}
          </div>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)', fontWeight: 800, color: '#fff', marginBottom: '0.75rem', lineHeight: 1.15 }}>Commercial Solar ROI Calculator</h1>
          <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.72)', maxWidth: 580, lineHeight: 1.65 }}>Enter your address — we'll pull real utility rates and NREL climate data to give you accurate numbers.</p>
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(245,166,35,0.18)', backdropFilter: 'blur(4px)', padding: '0.5rem 2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(245,166,35,0.9)', margin: 0, textAlign: 'center' }}>
            ✓ Commercial ITC (40%) and Bonus Depreciation remain fully active and are included in all calculations below.
          </p>
        </div>
      </div>

      {/* CALCULATOR */}
      <section style={{ padding: '3rem 2.5rem 5rem', background: 'var(--gray-50)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '2rem', alignItems: 'start' }}>

          {/* FORM */}
          <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,43,95,0.1)', padding: '2.5rem', border: '1px solid var(--gray-100)' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '1.5rem' }}>Tell us about your facility</span>

            <div style={{ marginBottom: '1.25rem', position: 'relative' }} ref={addrContainerRef}>
              <label style={labelStyle}>Street Address *</label>
              <input ref={addressRef} style={inputStyle} type="text" placeholder="123 Business Park Dr"
                onChange={e => { setForm({ ...form, address: e.target.value }); handleAddrInput(e.target.value); }}
                autoComplete="off" spellCheck="false" />
              {suggestOpen && suggestions.length > 0 && dropdownRect && (
                <div style={{ position: 'fixed', top: dropdownRect.bottom + 4, left: dropdownRect.left, width: dropdownRect.width, background: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(26,43,95,0.15)', zIndex: 99999, border: '1px solid var(--gray-200)' }}>
                  {suggestions.map((s, i) => (
                    <div key={i}
                      onMouseDown={e => { e.preventDefault(); handleAddrSelect(s); }}
                      style={{ padding: '0.65rem 1rem', fontSize: '0.9rem', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', color: 'var(--navy)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,166,35,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <span style={{ fontWeight: 600 }}>{getMainText(s)}</span>
                      <span style={{ color: 'var(--gray-400)', fontSize: '0.82rem', marginLeft: '0.4rem' }}>{getSecondaryText(s)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <label style={labelStyle}>City</label>
                <input style={inputStyle} type="text" placeholder="Los Angeles" value={form.city}
                  onChange={e => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>ZIP Code</label>
                <input style={inputStyle} type="text" placeholder="90001" maxLength={5} value={form.zip}
                  onChange={e => { setForm({ ...form, zip: e.target.value }); if (e.target.value.length === 5) setZipInfo(checkZip(e.target.value)); }}
                  onBlur={handleZipBlur} />
              </div>
            </div>



            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Average Monthly Electric Bill *</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', fontWeight: 600 }}>$</span>
                <input style={{ ...inputStyle, paddingLeft: '1.75rem' }} type="number" placeholder="2,500" min="100"
                  value={form.monthlyBill} onChange={e => setForm({ ...form, monthlyBill: e.target.value })} />
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Financing Preference</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.4rem' }}>
                {[
                  { val: 'full',  label: 'Full Payment',    sub: 'Best ROI · No interest' },
                  { val: 'loan',  label: 'PPA / Prepaid',   sub: '6% rate · 10-year term' },
                ].map(({ val, label, sub }) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1.1rem', borderRadius: 10, border: `1.5px solid ${form.financing === val ? 'var(--gold)' : 'var(--gray-200)'}`, cursor: 'pointer', background: form.financing === val ? 'rgba(245,166,35,0.08)' : 'var(--gray-50)', transition: 'all 0.2s' }}>
                    <input type="radio" name="commFinancing" value={val} checked={form.financing === val} onChange={() => setForm({ ...form, financing: val })} style={{ display: 'none' }} />
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${form.financing === val ? 'var(--gold)' : 'var(--gray-300)'}`, background: form.financing === val ? 'var(--gold)' : 'transparent', flexShrink: 0, transition: 'all 0.2s' }} />
                    <div>
                      <div style={{ fontWeight: form.financing === val ? 700 : 500, fontSize: '0.9rem', color: 'var(--navy)' }}>{label}</div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--gray-400)', marginTop: '0.1rem' }}>{sub}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--gray-100)', margin: '1.5rem 0' }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gray-400)', display: 'block', marginBottom: '1rem' }}>Contact Information</span>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Company Name</label>
              <input style={inputStyle} type="text" placeholder="Acme Corp" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelStyle}>Contact Name</label>
                <input style={inputStyle} type="text" placeholder="John Smith" value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input style={inputStyle} type="tel" placeholder="(213) 555-0100" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Email *</label>
              <input style={inputStyle} type="email" placeholder="john@acmecorp.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <input type="checkbox" id="consent" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 3, accentColor: 'var(--navy)', cursor: 'pointer', flexShrink: 0 }} />
              <label htmlFor="consent" style={{ fontSize: '0.82rem', color: 'var(--gray-600)', lineHeight: 1.55, cursor: 'pointer' }}>
                I agree to be contacted by a SolarSavings specialist regarding my commercial solar analysis. No spam, ever.
              </label>
            </div>

            <button onClick={handleSubmit} disabled={!consent || loading}
              style={{ width: '100%', padding: '1rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '1rem', border: 'none', cursor: consent ? 'pointer' : 'not-allowed', background: consent ? 'var(--gold)' : 'var(--gray-200)', color: consent ? 'var(--navy)' : 'var(--gray-400)', transition: 'all 0.3s' }}>
              {loading ? 'Analyzing...' : 'Analyze My Facility →'}
            </button>
            <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '0.6rem' }}>🔒 Your information is never sold or shared.</p>
          </div>

          {/* RESULTS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {!results && !loading && <CAMap variant="navy" />}
            <AnimatePresence mode="wait">
              {loading && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,43,95,0.1)', padding: '4rem 2rem', textAlign: 'center' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', border: '4px solid var(--gray-200)', borderTopColor: 'var(--gold)', animation: 'spin 0.8s linear infinite', margin: '0 auto 1.25rem' }} />
                  <p style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, color: 'var(--navy)', fontSize: '1.1rem' }}>{loadingMsg || 'Analyzing your facility...'}</p>
                  <p style={{ color: 'var(--gray-400)', fontSize: '0.875rem', marginTop: '0.4rem' }}>Real utility rates · NREL climate data · 25-year model</p>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </motion.div>
              )}

              {results && !loading && (
                <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,43,95,0.1)', overflow: 'hidden' }}>

                  <div style={{ background: 'var(--navy)', padding: '1.5rem 2rem', textAlign: 'center' }}>
                    <h3 style={{ fontFamily: 'Sora, sans-serif', color: '#fff', fontSize: '1.1rem', fontWeight: 700, textAlign: 'center' }}>Your Commercial Solar Analysis</h3>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', marginTop: '0.2rem', textAlign: 'center' }}>{form.company || form.address}</p>
                  </div>

                  <div style={{ padding: '1.75rem' }}>
                    {/* Data source tags */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                      {['NREL PVWatts', results.utilityName].map(tag => (
                        <span key={tag} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 20, background: 'rgba(245,166,35,0.1)', color: 'var(--gold)', border: '1px solid rgba(245,166,35,0.25)', letterSpacing: '0.04em' }}>{tag}</span>
                      ))}
                    </div>

                    {/* Key system stats */}
                    {results.pvCapacityFactor && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1.25rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 12, border: '1px solid var(--gray-100)' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>System Size</div>
                          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.1rem', color: 'var(--navy)' }}>{fmtDec(results.systemSizeKW)} kW</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>Capacity Factor</div>
                          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.1rem', color: 'var(--navy)' }}>{results.pvCapacityFactor}%</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--gray-400)' }}>NREL</div>
                        </div>
                      </div>
                    )}

                    {/* SGIP Badge — only show when ZIP qualifies */}
                    {results.sgipFlaggedForReview && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.875rem 1.1rem', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600, marginBottom: '1.25rem', background: 'rgba(245,166,35,0.1)', border: '1.5px solid rgba(245,166,35,0.35)', color: 'var(--navy)', lineHeight: 1.55 }}>
                        <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚡</span>
                        <span><strong>Your ZIP may qualify for SGIP Equity Resiliency</strong> — commercial SGIP requires specialist verification. <strong>A specialist will confirm within 1 business day.</strong></span>
                      </div>
                    )}

                    <div style={{ marginBottom: '0.75rem' }}>
                      <MetricCard label="Gross System Cost" value={fmt(results.systemCost)} />
                    </div>

                    {/* Incentives breakdown — between Gross and Net */}
                    <div style={{ background: 'linear-gradient(135deg, rgba(245,166,35,0.1), rgba(245,166,35,0.05))', border: '1.5px solid rgba(245,166,35,0.25)', borderRadius: 14, padding: '1.25rem', marginBottom: '0.75rem' }}>
                      <button onClick={() => setIncentivesOpen(o => !o)} style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, color: 'var(--navy)', fontSize: '0.9rem' }}>− Total Incentives: <span style={{ color: 'var(--gold)' }}>{fmt(results.totalIncentives)}</span></span>
                        <span style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>{incentivesOpen ? '▲' : '▼'}</span>
                      </button>
                      <AnimatePresence>
                        {incentivesOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} style={{ overflow: 'hidden' }}>
                            <div style={{ borderTop: '1px solid rgba(245,166,35,0.2)', marginTop: '1rem', paddingTop: '1rem' }}>
                              {[['Federal ITC (40%)', fmt(results.ITC)], ['Bonus Depreciation (85% × 21%)', fmt(results.bonusDepreciation)], ['SGIP Rebate', '⏳ Pending specialist review']].map(([l, v]) => (
                                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', padding: '0.4rem 0', color: 'var(--gray-600)' }}>
                                  <span>{l}</span><span style={{ fontWeight: 600, color: 'var(--navy)' }}>{v}</span>
                                </div>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <MetricCard label="Net Cost After All Incentives" value={fmt(results.netSystemCost)} big />
                    </div>
                    <div style={{ height: 1, background: 'var(--gray-100)', margin: '1.25rem 0' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <MetricCard label="Annual Electricity Savings" value={fmt(results.annualElecSavings)} />
                      <MetricCard label="Annual LCFS Income" value={fmt(results.annualLCFS)} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <MetricCard label="Payback Period" value={fmtDec(results.paybackYears) + ' yrs'} />
                      <MetricCard label="25-Year IRR" value={fmtDec(results.IRR25yr) + '%'} gold />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <MetricCard label="25-Year Net Savings" value={fmt(results.savings25yr)} gold big />
                    </div>

                    {/* Carbon — single tree card */}
                    <div style={{ padding: '1.25rem', borderRadius: 12, background: 'linear-gradient(135deg, rgba(34,197,94,0.09), rgba(34,197,94,0.04))', border: '1.5px solid rgba(34,197,94,0.28)', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ fontSize: '2.4rem', lineHeight: 1, flexShrink: 0 }}>🌳</div>
                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>Environmental Impact (25 Years)</div>
                        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '1.2rem', color: 'var(--navy)', lineHeight: 1.3 }}>
                          Equivalent to planting{' '}
                          <span style={{ color: '#16a34a' }}>{Math.round(results.carbonTons25yr * 45).toLocaleString()} trees</span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: '0.2rem' }}>
                          {results.carbonTons25yr.toLocaleString()} metric tons CO₂ avoided · EPA carbon sequestration data
                        </div>
                      </div>
                    </div>

                    {/* Monthly chart */}
                    {results.monthlyPV?.length > 0 && (
                      <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem', border: '1px solid var(--gray-100)' }}>
                        <MonthlyChart data={results.monthlyPV} />
                      </div>
                    )}

                    {results.isLoan && (
                      <div style={{ background: 'var(--navy)', borderRadius: 12, padding: '1.25rem', marginBottom: '1rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div style={{ padding: '1.25rem', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Monthly Loan Payment</div>
                            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#fff' }}>{fmt(results.monthlyPayment)}</div>
                          </div>
                          <div style={{ padding: '1.25rem', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>Monthly Net Cash Flow</div>
                            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: results.monthlyNetCashFlow >= 0 ? 'var(--green)' : 'var(--amber)' }}>{(results.monthlyNetCashFlow >= 0 ? '+' : '') + fmt(results.monthlyNetCashFlow)}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ background: 'rgba(245,166,35,0.08)', border: '1.5px solid rgba(245,166,35,0.2)', borderRadius: 12, padding: '1rem 1.25rem', fontSize: '0.85rem', color: 'var(--navy)', lineHeight: 1.6, marginTop: '0.5rem' }}>
                      📍 Based on your ZIP, you may also qualify for the <strong>Energy Community Bonus (+10% ITC)</strong>. Our specialist will verify in your free engineer report.
                    </div>

                    <p style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: '1rem', lineHeight: 1.6 }}>
                      * Data powered by {results.dataSource}. Actual figures vary. A certified specialist will provide exact numbers.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </>
  );
}
